<template>
  <Section>
    <SectionHeader :title="$t('apps.billing.message.usagedetails')"/>
    <FormRow>
      <Col
        v-if="filterUsageOptions.length > 1"
        className="col-lg-3 mt-2"
      >
      <select
        v-model="tabSelected"
        class="form-control custom-select"
      >
        <option
          v-for="(item, index) in filterUsageOptions"
          :key="index"
          :value="item.value"
        >
          {{ item.text }}
        </option>
      </select>
      </Col>
      <Col
        v-if="loading"
        className="col-lg-12">
      <BaseLoading />
      </Col>
      <template v-else>
        <Col className="col-lg-12">
        <span>
          {{
            $t('apps.billing.message.datetimeperiod', {
              start: getDateTimeUTC(dateStart),
              end: getDateTimeUTC(dateEnd),
            })
          }}
        </span>
        </Col>
        <Col className="col-lg-12">
        <BaseTableUsegeBranch
          v-if="selected.branches"
          :items="branches"
        />
        <BaseTableUsegeWarehouse
          v-if="selected.warehouses"
          :items="warehouses"
        />
        <BaseTableUsegeUser
          v-if="selected.users"
          :items="users"
        />
        <BaseTableUsegeApp
          v-if="selected.records"
          :items="records"
        />
        <BaseTableUsegeFile
          v-if="selected.files"
          :items="files"
        />
        <BaseTableUsegeMessageLine
          v-if="selected.lineMessages"
          :items="lineMessages"
        />
        <BaseTableUsegeEmail
          v-if="selected.emails"
          :items="emails"
        />
        <BaseTableUsegeQRCode
          v-if="selected.qrcodes"
          :items="qrcodes"
        />
        <template
          v-for="(app, index) in HAS_FEE_APP_MODULES"
        >
          <BaseTableUsegeAppModule
            v-if="selected[`APPID${app.app_id}_MODULEID${app.module_id}`]"
            :items="apps[`APPID${app.app_id}_MODULEID${app.module_id}`]"
            :key="index"
          />
        </template>
        </Col>
      </template>
    </FormRow>
  </Section>
</template>

<script>
import { BILLING_SERVICE_USAGE, HAS_FEE_APP_MODULES } from '../../helpers/enum'

export default {
  props: {
    value: {
      type: [Object, String],
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    dateStart: {
      required: true,
      type: String,
      default: null,
    },
    dateEnd: {
      required: true,
      type: String,
      default: null,
    },
    records: {
      required: true,
      type: Array,
      default: () => ([]),
    },
    files: {
      required: true,
      type: Array,
      default: () => ([]),
    },
    lineMessages: {
      required: true,
      type: Array,
      default: () => ([]),
    },
    emails: {
      required: true,
      type: Array,
      default: () => ([]),
    },
    qrcodes: {
      required: true,
      type: Array,
      default: () => ([]),
    },
    branches: {
      required: true,
      type: Array,
      default: () => ([]),
    },
    warehouses: {
      required: true,
      type: Array,
      default: () => ([]),
    },
    users: {
      required: true,
      type: Array,
      default: () => ([]),
    },
    apps: {
      required: true,
      type: Object,
      default: () => ({}),
    },
  },

  data () {
    return {
      HAS_FEE_APP_MODULES,
      tabSelected: BILLING_SERVICE_USAGE.RECORD,
    }
  },

  computed: {
    selected () {
      const result = {
        records: this.tabSelected === BILLING_SERVICE_USAGE.RECORD,
        files: this.tabSelected === BILLING_SERVICE_USAGE.FILE,
        lineMessages: this.tabSelected === BILLING_SERVICE_USAGE.MESSAGE_LINE,
        emails: this.tabSelected === BILLING_SERVICE_USAGE.EMAIL,
        qrcodes: this.tabSelected === BILLING_SERVICE_USAGE.QRCODE,
        branches: this.tabSelected === BILLING_SERVICE_USAGE.BRANCH,
        warehouses: this.tabSelected === BILLING_SERVICE_USAGE.WAREHOUSE,
        users: this.tabSelected === BILLING_SERVICE_USAGE.USER,
      }
      HAS_FEE_APP_MODULES.map((item) => {
        const key = `APPID${item.app_id}_MODULEID${item.module_id}`
        result[key] = this.tabSelected === key
      })
      return result
    },
    usageOptions () {
      const records = {
        text: this.$t('apps.billing.message.application'),
        value: BILLING_SERVICE_USAGE.RECORD,
      }
      const files = {
        text: this.$t('apps.billing.message.file'),
        value: BILLING_SERVICE_USAGE.FILE,
      }
      const lineMessages = {
        text: this.$t('apps.billing.message.linemessage'),
        value: BILLING_SERVICE_USAGE.MESSAGE_LINE,
      }
      const emails = {
        text: this.$t('apps.billing.message.email'),
        value: BILLING_SERVICE_USAGE.EMAIL,
      }
      const qrcodes = {
        text: this.$t('apps.billing.message.qrcode'),
        value: BILLING_SERVICE_USAGE.QRCODE,
      }
      const branches = {
        text: this.$t('apps.billing.message.branch'),
        value: BILLING_SERVICE_USAGE.BRANCH,
      }
      const warehouses = {
        text: this.$t('apps.billing.message.warehouse'),
        value: BILLING_SERVICE_USAGE.WAREHOUSE,
      }
      const users = {
        text: this.$t('apps.billing.message.user'),
        value: BILLING_SERVICE_USAGE.USER,
      }
      const apps = HAS_FEE_APP_MODULES.reduce((acc, cur) => {
        const key = `APPID${cur.app_id}_MODULEID${cur.module_id}`
        return {
          ...acc,
          [key]: {
            text: (
              this.$t(`apps.billing.apps.appid${cur.app_id}`) + ' -> ' +
                this.$t(`apps.billing.apps.appid${cur.app_id}-moduleid${cur.module_id}`)
            ),
            value: key,
          },
        }
      }, {})
      return {
        ...apps,
        records,
        files,
        lineMessages,
        emails,
        qrcodes,
        branches,
        warehouses,
        users,
      }
    },
    filterUsageOptions () {
      const keys = [
        'branches',
        'warehouses',
        'users',
        'records',
        'files',
        'lineMessages',
        'emails',
        'qrcodes',
        ...HAS_FEE_APP_MODULES.map((app) => `APPID${app.app_id}_MODULEID${app.module_id}`),
      ]
      return keys.reduce((acc, key) => {
        if (key.search('APPID') != -1) {
          if (this.apps[key].length) {
            acc.push(this.usageOptions[key])
          }
        } else if (this[key].length) {
          acc.push(this.usageOptions[key])
        }
        return acc
      }, [])
    },
  },

  watch: {
    tabSelected () {
      this.$emit('input', this.tabSelected)
    },
    value () {
      this.tabSelected = this.value
    },
  },

  mounted () {
    this.tabSelected = this.value
  },

  methods: {
    handleTabChanged ({ value }) {
      this.tabSelected = value
      this.$emit('change', value)
    },
  },
}
</script>



// WEBPACK FOOTER //
// src/components/combine/BillingInvoiceUsage.vue